const DAY_MS = 86400000;

export function reportDateKey(now = new Date()) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Rome', year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(now);
  const value = (type) => parts.find((part) => part.type === type).value;
  return `${value('year')}-${value('month')}-${value('day')}`;
}

export function reportPeriodRange(period, today = reportDateKey()) {
  const date = new Date(`${today}T00:00:00Z`);
  if (period === 'week') date.setUTCDate(date.getUTCDate() - (date.getUTCDay() + 6) % 7);
  if (period === 'month') date.setUTCDate(1);
  if (period === 'last30') date.setUTCDate(date.getUTCDate() - 29);
  return { from: date.toISOString().slice(0, 10), to: today };
}

export function reportRangeError({ from, to }) {
  const valid = (value) => {
    const time = Date.parse(`${value}T00:00:00Z`);
    return /^\d{4}-\d{2}-\d{2}$/.test(value) && Number.isFinite(time)
      && new Date(time).toISOString().slice(0, 10) === value;
  };
  if (!valid(from) || !valid(to)) return 'invalidDates';
  if (from > to) return 'reversedDates';
  if (Date.parse(to) - Date.parse(from) > 365 * DAY_MS) return 'tooLong';
  return '';
}

const numeric = (value) => typeof value === 'number' && Number.isFinite(value);
const fields = (value, keys) => keys.every((key) => numeric(value?.[key]));
const percentage = (value) => value === null || numeric(value);
const amount = (value) => fields(value, ['net', 'vat', 'gross']);

export function validateSpaReport(data, type, range) {
  let valid = data?.range?.from === range.from && data?.range?.to === range.to
    && typeof data.range.timeZone === 'string';
  if (type === 'operational') {
    valid = valid && Array.isArray(data.treatmentCounts)
      && data.treatmentCounts.every((row) => fields(row, ['treatmentId', 'count']))
      && ['operatorUtilization', 'cabinUtilization'].every((key) => Array.isArray(data[key])
        && data[key].every((row) => fields(row, ['treatmentCount', 'busyMinutes', 'availableMinutes'])
          && percentage(row.utilizationPercent) && row[key === 'operatorUtilization' ? 'operator' : 'cabin']?.id != null))
      && numeric(data.occupancy?.spa?.usedPersonMinutes)
      && percentage(data.occupancy?.spa?.capacityPersonMinutes)
      && percentage(data.occupancy?.spa?.occupancyPercent)
      && Array.isArray(data.occupancy?.areas)
      && data.occupancy.areas.every((row) => row.area?.id != null
        && fields(row, ['usedPersonMinutes', 'capacityPersonMinutes']) && percentage(row.occupancyPercent))
      && fields(data.capacity, ['openingMinutes', 'plannerIntervalMinutes'])
      && percentage(data.capacity?.total) && percentage(data.capacity?.perInterval)
      && fields(data.outcomes, ['appointments', 'cancellations', 'noShows'])
      && percentage(data.outcomes?.cancellationPercent) && percentage(data.outcomes?.noShowPercent);
  } else {
    valid = valid && amount(data.revenue?.total) && amount(data.revenue?.treatments?.total)
      && fields(data.revenue?.treatments, ['count', 'averageGross'])
      && ['byTreatment', 'byOperator', 'byCabin', 'byCustomerType'].every((key) =>
        Array.isArray(data.revenue.treatments[key]) && data.revenue.treatments[key].every((row) => amount(row) && numeric(row.count)
          && (key !== 'byOperator' || row.operator?.id != null)
          && (key !== 'byCabin' || row.cabin?.id != null)
          && (key !== 'byCustomerType' || typeof row.customerType === 'string')))
      && amount(data.revenue?.retail?.total)
      && fields(data.revenue?.retail, ['orderCount', 'itemQuantity', 'averageOrderGross'])
      && Array.isArray(data.revenue?.retail?.byProduct)
      && data.revenue.retail.byProduct.every((row) => amount(row) && numeric(row.quantity))
      && fields(data.costs, ['treatments', 'retail', 'total'])
      && fields(data.commissions, ['treatments', 'retail', 'total'])
      && fields(data.margin, ['netRevenue', 'productCosts', 'commissions', 'amount'])
      && percentage(data.margin?.percent)
      && Array.isArray(data.usage?.packages) && Array.isArray(data.usage?.storedValue)
      && data.usage.packages.every((row) => fields(row, ['consumedQuantity', 'reversedQuantity', 'netQuantity']))
      && data.usage.storedValue.every((row) => fields(row, ['consumedAmount', 'reversedAmount', 'netAmount']));
  }
  if (!valid) throw new Error(`Invalid SPA ${type} report response`);
  return data;
}

// Export aggregates independently: cabin/operator breakdowns can attribute the same treatment more than once.
export function spaReportsCsv(operational, financial) {
  const rows = [['report', 'metric', 'value']];
  const visit = (report, value, path = '') => {
    if (value !== null && typeof value === 'object') {
      Object.entries(value).forEach(([key, item]) => visit(report, item, path ? `${path}.${key}` : key));
    } else rows.push([report, path, value ?? '']);
  };
  visit('operational', operational);
  visit('financial', financial);
  const cell = (value) => {
    let text = String(value);
    if (typeof value === 'string' && /^[\s]*[=+\-@]/.test(text)) text = `'${text}`;
    return `"${text.replace(/"/g, '""')}"`;
  };
  return '\uFEFF' + rows.map((row) => row.map(cell).join(',')).join('\r\n');
}
